import { boardingIcon, daycareIcon, groomingIcon, trainingIcon, walkingIcon } from 'assets/images';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { OptionCard } from 'components/UI/OptionCard/OptionCard';
import { PromptModal } from 'components/UI/PromptModal/PromptModal';
import { useMarketplace } from 'hooks/useMarketplace';
import { useOnlineBookingState } from 'hooks/useOnlineBookingState';
import React from 'react';
import { useSelector } from 'react-redux';
import {
    getEvaluationServices,
    getHasDentalService,
    getHasTrainingService,
    getHasWalkingService
} from 'reducers/services/ServicesSelectors';
import { dispatch } from 'store';
import { ReportEvaluationResult } from 'types/Evaluation';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { v4 as uuid } from 'uuid';
import {
    navigateToStep,
    setOnlineBookingEvaluationServiceCategory,
    setOnlineBookingItems,
    setOnlineBookingSelectedCategory
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { PurchaseBanner } from 'components/OnlineBooking/Purchase/Banner/PurchaseBanner';
import {
    getOnlineBookingBoardingItemsWithEvaluation,
    getOnlineBookingDaycareItemsWithEvaluation,
    getOnlineBookingGroomingItemsWithEvaluation,
    getOnlineBookingTrainingItems,
    getOnlineBookingWalkingItems
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import {
    isOnlineBookingEvaluationItem,
    OnlineBookingItem,
    OnlineBookingStep
} from 'types/OnlineBooking';

const PURCHASE_ENABLED_MARKETPLACE_IDS = [7222, 33];

const getTotalItems = (items: Array<OnlineBookingItem>) => {
    return items.reduce((total, item) => {
        if (item.service) {
            total += 1;
        }

        for (let i = 0; i < item.addons.length; i++) {
            total += 1;
        }

        return total;
    }, 0);
};

export const OnlineBookingServiceCategoryStep: React.FC = () => {
    const marketplace = useMarketplace();
    const { selectedPets, bookingItems } = useOnlineBookingState();
    const evaluationServices = useSelector(getEvaluationServices);
    const hasDentalService = useSelector(getHasDentalService);
    const hasWalkingService = useSelector(getHasWalkingService);
    const hasTrainingService = useSelector(getHasTrainingService);

    const groomingBookingItems = useSelector(getOnlineBookingGroomingItemsWithEvaluation);
    const hasGroomingBookingItems = groomingBookingItems.length > 0;
    const groomingPetNames = groomingBookingItems.map(item => item.pet.name).join(', ');
    const groomingTotalItems = getTotalItems(groomingBookingItems);

    const daycareBookingItems = useSelector(getOnlineBookingDaycareItemsWithEvaluation);
    const hasDaycareBookingItems = daycareBookingItems.length > 0;
    const daycarePetNames = daycareBookingItems.map(item => item.pet.name).join(', ');
    const daycareTotalItems = getTotalItems(daycareBookingItems);

    const boardingBookingItems = useSelector(getOnlineBookingBoardingItemsWithEvaluation);
    const hasBoardingBookingItems = boardingBookingItems.length > 0;
    const boardingPetNames = boardingBookingItems.map(item => item.pet.name).join(', ');
    const boardingTotalItems = getTotalItems(boardingBookingItems);

    const walkingBookingItems = useSelector(getOnlineBookingWalkingItems);
    const hasWalkingBookingItems = walkingBookingItems.length > 0;
    const walkingPetNames = walkingBookingItems.map(item => item.pet.name).join(', ');
    const walkingTotalItems = getTotalItems(walkingBookingItems);

    const trainingBookingItems = useSelector(getOnlineBookingTrainingItems);
    const hasTrainingBookingItems = trainingBookingItems.length > 0;
    const trainingPetNames = trainingBookingItems.map(item => item.pet.name).join(', ');
    const trainingTotalItems = getTotalItems(trainingBookingItems);

    const isValid = React.useMemo(() => {
        return (
            hasGroomingBookingItems ||
            hasDaycareBookingItems ||
            hasBoardingBookingItems ||
            hasWalkingBookingItems ||
            hasTrainingBookingItems
        );
    }, [
        hasGroomingBookingItems,
        hasDaycareBookingItems,
        hasBoardingBookingItems,
        hasWalkingBookingItems,
        hasTrainingBookingItems
    ]);

    const [showBackConfirm, setShowBackConfirm] = React.useState(false);

    const backHandler = React.useCallback(() => {
        if (bookingItems.length > 0) {
            setShowBackConfirm(true);
        } else {
            dispatch(navigateToStep(OnlineBookingStep.PetList));
        }
    }, [bookingItems]);

    const confirmBackHandler = React.useCallback(() => {
        dispatch(setOnlineBookingItems([]));
        dispatch(navigateToStep(OnlineBookingStep.PetList));
    }, []);

    const [showPurchaseConfirm, setShowPurchaseConfirm] = React.useState(false);

    const purchaseHandler = React.useCallback(() => {
        if (bookingItems.length > 0) {
            setShowPurchaseConfirm(true);
        } else {
            dispatch(navigateToStep(OnlineBookingStep.Purchase));
        }
    }, [bookingItems]);

    const confirmPurchaseHandler = React.useCallback(() => {
        dispatch(setOnlineBookingItems([]));
        dispatch(navigateToStep(OnlineBookingStep.Purchase));
    }, []);

    const deleteHandler = (category: MarketplaceServiceCategory | MarketplaceServiceCategory[]) => {
        const categories = Array.isArray(category) ? category : [category];

        dispatch(
            setOnlineBookingItems(
                bookingItems.filter(item => {
                    // Remove if businessArea matches any category
                    if (categories.includes(item.businessArea)) {
                        return false;
                    }

                    // Remove if it's an evaluation for the specified category
                    if (isOnlineBookingEvaluationItem(item)) {
                        return !categories.includes(item.evaluationBusinessArea);
                    }

                    return true;
                })
            )
        );
    };

    const shouldRouteToEvaluation = (
        category: MarketplaceServiceCategory,
        evaluationField: 'groomingResult' | 'daycareResult' | 'boardingResult'
    ): boolean => {
        const isEvaluationsEnabled =
            marketplace.evaluationEnabled &&
            evaluationServices.some(
                service => service.evaluationMetadata?.businessAreas.includes(category)
            );

        return (
            isEvaluationsEnabled &&
            selectedPets.every(
                pet =>
                    !pet.evaluationReport ||
                    pet.evaluationReport?.[evaluationField] === ReportEvaluationResult.FAIL
            )
        );
    };

    const getCategoryGroup = (category: MarketplaceServiceCategory) => {
        if (category === MarketplaceServiceCategory.GROOMING) {
            return [MarketplaceServiceCategory.GROOMING, MarketplaceServiceCategory.DENTAL];
        }

        return [category];
    };

    const continueWithCategoryHandler = (category: MarketplaceServiceCategory) => {
        const categoryGroup = getCategoryGroup(category);

        // Map categories to their evaluation fields
        const evaluationFieldMap: Partial<
            Record<
                MarketplaceServiceCategory,
                'groomingResult' | 'daycareResult' | 'boardingResult'
            >
        > = {
            [MarketplaceServiceCategory.GROOMING]: 'groomingResult',
            [MarketplaceServiceCategory.DAYCARE]: 'daycareResult',
            [MarketplaceServiceCategory.BOARDING]: 'boardingResult'
        };

        // Check if this category supports evaluations and route accordingly
        const evaluationField = evaluationFieldMap[category];
        if (evaluationField && shouldRouteToEvaluation(category, evaluationField)) {
            // Route to evaluation flow
            const evaluationBookingItems: Array<OnlineBookingItem> = selectedPets.map(pet => ({
                uuid: uuid(),
                pet,
                addons: [],
                businessArea: MarketplaceServiceCategory.EVALUATION,
                evaluationBusinessArea: category,
                service: undefined
            }));

            dispatch(setOnlineBookingSelectedCategory(MarketplaceServiceCategory.EVALUATION));
            dispatch(setOnlineBookingEvaluationServiceCategory(category));
            dispatch(
                setOnlineBookingItems([
                    ...bookingItems.filter(
                        item => item.businessArea !== MarketplaceServiceCategory.EVALUATION
                    ),
                    ...evaluationBookingItems
                ])
            );
            dispatch(navigateToStep(OnlineBookingStep.ServiceList));
            return;
        }

        // Normal flow
        const selectedPetsBookingItems: Array<OnlineBookingItem> = [
            ...bookingItems.filter(item => !categoryGroup.includes(item.businessArea)),
            ...selectedPets.map(pet => {
                const existingItem = bookingItems.find(
                    item => item.pet.uuid === pet.uuid && categoryGroup.includes(item.businessArea)
                );

                return {
                    uuid: uuid(),
                    pet,
                    addons: [],
                    businessArea: category,
                    service: undefined,

                    // Keep existing item if it exists
                    ...existingItem
                } as OnlineBookingItem;
            })
        ];

        dispatch(setOnlineBookingSelectedCategory(category));
        dispatch(setOnlineBookingItems(selectedPetsBookingItems));

        dispatch(navigateToStep(OnlineBookingStep.ServiceList));
    };

    const submitHandler = React.useCallback(() => {
        dispatch(navigateToStep(OnlineBookingStep.Agreements));
    }, []);

    return (
        <>
            <PromptModal
                open={showBackConfirm}
                title="Change selected pets?"
                description="Going back will clear your current service selections. This action cannot be undone."
                onConfirm={confirmBackHandler}
                onCancel={() => setShowBackConfirm(false)}
            />
            <PromptModal
                open={showPurchaseConfirm}
                title="Discard Unsaved Changes?"
                description="Continuing to Packages & Memberships will discard your selected services. Do you want to proceed?"
                confirmLabel="Yes, Continue"
                confirmVariant="green"
                onConfirm={confirmPurchaseHandler}
                onCancel={() => setShowPurchaseConfirm(false)}
            />
            <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
                <LeftColumn>
                    <>
                        <h1 className="h2 underlined w-full mb-6">Select service category</h1>

                        <div className="flex flex-col gap-4">
                            {marketplace.settings.groomingEnabled && (
                                <OptionCard
                                    title={hasDentalService ? 'Grooming & Dental' : 'Grooming'}
                                    imageSrc={groomingIcon}
                                    subtitle={groomingPetNames}
                                    description={
                                        hasGroomingBookingItems
                                            ? `${groomingTotalItems} items`
                                            : undefined
                                    }
                                    onClick={() =>
                                        continueWithCategoryHandler(
                                            MarketplaceServiceCategory.GROOMING
                                        )
                                    }
                                    onDelete={
                                        hasGroomingBookingItems
                                            ? () =>
                                                  deleteHandler([
                                                      MarketplaceServiceCategory.GROOMING,
                                                      MarketplaceServiceCategory.DENTAL
                                                  ])
                                            : undefined
                                    }
                                />
                            )}

                            {marketplace.settings.daycareEnabled && (
                                <OptionCard
                                    title="Daycare"
                                    imageSrc={daycareIcon}
                                    subtitle={daycarePetNames}
                                    description={
                                        hasDaycareBookingItems
                                            ? `${daycareTotalItems} items`
                                            : undefined
                                    }
                                    onClick={() =>
                                        continueWithCategoryHandler(
                                            MarketplaceServiceCategory.DAYCARE
                                        )
                                    }
                                    onDelete={
                                        hasDaycareBookingItems
                                            ? () =>
                                                  deleteHandler(MarketplaceServiceCategory.DAYCARE)
                                            : undefined
                                    }
                                />
                            )}

                            {marketplace.settings.boardingEnabled && (
                                <OptionCard
                                    title="Boarding"
                                    imageSrc={boardingIcon}
                                    subtitle={boardingPetNames}
                                    description={
                                        hasBoardingBookingItems
                                            ? `${boardingTotalItems} items`
                                            : undefined
                                    }
                                    onClick={() =>
                                        continueWithCategoryHandler(
                                            MarketplaceServiceCategory.BOARDING
                                        )
                                    }
                                    onDelete={
                                        hasBoardingBookingItems
                                            ? () =>
                                                  deleteHandler(MarketplaceServiceCategory.BOARDING)
                                            : undefined
                                    }
                                />
                            )}

                            {marketplace.settings.trainingEnabled && hasTrainingService && (
                                <OptionCard
                                    title="Training"
                                    imageSrc={trainingIcon}
                                    subtitle={trainingPetNames}
                                    description={
                                        hasTrainingBookingItems
                                            ? `${trainingTotalItems} items`
                                            : undefined
                                    }
                                    onClick={() =>
                                        continueWithCategoryHandler(
                                            MarketplaceServiceCategory.TRAINING
                                        )
                                    }
                                    onDelete={
                                        hasTrainingBookingItems
                                            ? () =>
                                                  deleteHandler(MarketplaceServiceCategory.TRAINING)
                                            : undefined
                                    }
                                />
                            )}

                            {marketplace.settings.walkingEnabled && hasWalkingService && (
                                <OptionCard
                                    title="Walking"
                                    imageSrc={walkingIcon}
                                    subtitle={walkingPetNames}
                                    description={
                                        hasWalkingBookingItems
                                            ? `${walkingTotalItems} items`
                                            : undefined
                                    }
                                    onClick={() =>
                                        continueWithCategoryHandler(
                                            MarketplaceServiceCategory.WALKING
                                        )
                                    }
                                    onDelete={
                                        hasWalkingBookingItems
                                            ? () =>
                                                  deleteHandler(MarketplaceServiceCategory.WALKING)
                                            : undefined
                                    }
                                />
                            )}
                        </div>

                        {PURCHASE_ENABLED_MARKETPLACE_IDS.includes(marketplace.id) && (
                            <div className="mt-6">
                                <PurchaseBanner onPurchase={purchaseHandler} />
                            </div>
                        )}

                        <div className="mt-auto hide-on-mobile-and-tablet pt-6">
                            <PoweredByCuddles />
                        </div>
                    </>
                </LeftColumn>

                <RightColumn
                    poweredByCuddles
                    footer={<OnlineBookingFooter disabled={!isValid} onSubmit={submitHandler} />}
                >
                    <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                        <MarketplaceInfo inverted marketplace={marketplace} />

                        <OnlineBookingResume />
                    </div>
                </RightColumn>
            </TwoColumnsLayout>
        </>
    );
};
